import { useContext, useState } from "react";
import { Button, Container, Card, Row, Col, Badge } from "react-bootstrap";
import UserContext from "../context/UserContext";
import ChangePasswordModal from "../components/ChangePasswordModal";

function Profile() {
  const { user } = useContext(UserContext);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  // Dynamic theme based on user role
  const getProfileTheme = () => {
    if (user?.isAdmin) {
      return {
        primary: "#6f42c1",
        secondary: "#495057",
        gradient: "linear-gradient(135deg, #6f42c1 0%, #495057 100%)",
        lightGradient: "linear-gradient(135deg, #e8e6ff 0%, #f0efff 100%)",
        icon: "fa-user-shield",
        title: "Admin Profile",
        badgeColor: "primary",
      };
    }
    return {
      primary: "#28a745",
      secondary: "#20c997",
      gradient: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
      lightGradient: "linear-gradient(135deg, #e8f5e8 0%, #f0f8f0 100%)",
      icon: "fa-user",
      title: "Profile Overview",
      badgeColor: "success",
    };
  };

  const theme = getProfileTheme();

  return (
    <div className="profile-page-wrapper">
      {/* Profile Hero Section */}
      <div className="profile-hero-section">
        <Container>
          <div className="text-center py-5">
            <div className="profile-avatar-container mx-auto mb-4">
              <div className="avatar-circle">
                <i className={`fas ${theme.icon} text-white`}></i>
              </div>
              {user?.isAdmin && (
                <div className="admin-crown">
                  <i className="fas fa-crown"></i>
                </div>
              )}
            </div>
            <h1 className="display-5 fw-bold text-white mb-2">{theme.title}</h1>
            <p className="lead text-white-50 mb-4">
              {user?.isAdmin
                ? "System Administrator Dashboard"
                : "Manage your personal information and settings"}
            </p>
            <div className="user-status-badges">
              <Badge className="status-badge me-2">
                <i className="fas fa-check-circle me-1"></i>
                Verified Account
              </Badge>
              {user?.isAdmin && (
                <Badge className="admin-status-badge">
                  <i className="fas fa-shield-alt me-1"></i>
                  Administrator
                </Badge>
              )}
            </div>
          </div>
        </Container>
      </div>

      {/* Profile Content */}
      <Container className="profile-content-section mt-5">
        <Row className="justify-content-center">
          <Col lg={8} xl={7}>
            <Card className="profile-card border-0 shadow-lg">
              <Card.Header className="profile-card-header border-0">
                <div className="d-flex align-items-center">
                  <div className="header-icon me-3">
                    <i className="fas fa-id-card text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-white mb-0 fw-bold">
                      Personal Information
                    </h4>
                    <small className="text-white-50">
                      Your account details and preferences
                    </small>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="g-4">
                  <Col md={12}>
                    <div className="info-field">
                      <label className="field-label">
                        <i className="fas fa-user me-2"></i>
                        Full Name
                      </label>
                      <div className="field-value">
                        <div className="value-display">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="field-icon">
                          <i className="fas fa-check-circle text-success"></i>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="info-field">
                      <label className="field-label">
                        <i className="fas fa-envelope me-2"></i>
                        Email Address
                      </label>
                      <div className="field-value">
                        <div className="value-display">{user.email}</div>
                        <div className="field-icon">
                          <i className="fas fa-shield-alt text-primary"></i>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="info-field">
                      <label className="field-label">
                        <i className="fas fa-phone me-2"></i>
                        Mobile Number
                      </label>
                      <div className="field-value">
                        <div className="value-display">{user.mobileNo}</div>
                        <div className="field-icon">
                          <i className="fas fa-check-circle text-success"></i>
                        </div>
                      </div>
                    </div>
                  </Col>
                  {user?.isAdmin && (
                    <Col md={12}>
                      <div className="admin-privileges-section">
                        <h5 className="section-title">
                          <i className="fas fa-key me-2"></i>
                          Administrative Privileges
                        </h5>
                        <div className="privileges-grid">
                          <div className="privilege-item">
                            <i className="fas fa-users-cog"></i>
                            <span>User Management</span>
                          </div>
                          <div className="privilege-item">
                            <i className="fas fa-box"></i>
                            <span>Product Management</span>
                          </div>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </Card.Body>
              <Card.Footer className="profile-card-footer border-0 bg-transparent">
                <div className="d-flex justify-content-between align-items-center">
                  <Button
                    onClick={() => setShowChangePasswordModal(true)}
                    className="change-password-btn"
                    size="lg"
                  >
                    <i className="fas fa-key me-2"></i>
                    Change Password
                  </Button>
                </div>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>

      <ChangePasswordModal
        show={showChangePasswordModal}
        handleClose={() => setShowChangePasswordModal(false)}
      />

      {/* Enhanced Custom Styles */}
      <style jsx>{`
        .profile-page-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        }

        .profile-hero-section {
          background: ${theme.gradient};
          position: relative;
          overflow: hidden;
          margin-top: -1rem;
          padding-top: 2rem;
        }

        .profile-hero-section::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="profilePattern" width="25" height="25" patternUnits="userSpaceOnUse"><circle cx="12.5" cy="12.5" r="1" fill="white" opacity="0.1"/><circle cx="6" cy="19" r="0.5" fill="white" opacity="0.15"/><circle cx="19" cy="6" r="0.5" fill="white" opacity="0.15"/></pattern></defs><rect width="100" height="100" fill="url(%23profilePattern)"/></svg>');
          pointer-events: none;
        }

        .profile-avatar-container {
          position: relative;
          width: 120px;
          height: 120px;
        }

        .avatar-circle {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          width: 120px;
          height: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);
          border: 4px solid rgba(255, 255, 255, 0.3);
          font-size: 3rem;
          animation: avatarFloat 3s ease-in-out infinite;
        }

        .admin-crown {
          position: absolute;
          top: -10px;
          right: -5px;
          background: linear-gradient(135deg, #ffd700, #ffed4e);
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #d4a853;
          font-size: 1.2rem;
          border: 3px solid white;
          animation: crownGlow 2s ease-in-out infinite alternate;
        }

        .user-status-badges {
          display: flex;
          justify-content: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .status-badge {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 25px;
          border: 1px solid rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          font-size: 0.9rem;
          font-weight: 600;
        }

        .admin-status-badge {
          background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.3),
            rgba(255, 255, 255, 0.1)
          );
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 25px;
          border: 2px solid rgba(255, 255, 255, 0.4);
          backdrop-filter: blur(10px);
          font-size: 0.9rem;
          font-weight: 600;
          animation: adminBadgeGlow 2s ease-in-out infinite alternate;
        }

        .profile-content-section {
          position: relative;
          z-index: 1;
          margin-top: -3rem;
          padding-bottom: 3rem;
        }

        .profile-card {
          border-radius: 25px;
          overflow: hidden;
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1) !important;
          backdrop-filter: blur(10px);
        }

        .profile-card-header {
          background: ${theme.gradient};
          padding: 2rem;
          position: relative;
        }

        .profile-card-header::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="cardPattern" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23cardPattern)"/></svg>');
          pointer-events: none;
        }

        .header-icon {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);
          font-size: 1.2rem;
        }

        .info-field {
          background: ${theme.lightGradient};
          border-radius: 15px;
          padding: 1.5rem;
          border: 2px solid transparent;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .info-field::before {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.2),
            transparent
          );
          transition: left 0.5s ease;
        }

        .info-field:hover {
          border-color: ${theme.primary};
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .info-field:hover::before {
          left: 100%;
        }

        .field-label {
          color: ${theme.primary};
          font-weight: 600;
          font-size: 0.9rem;
          margin-bottom: 0.75rem;
          display: flex;
          align-items: center;
        }

        .field-value {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .value-display {
          font-size: 1.1rem;
          font-weight: 600;
          color: #495057;
          flex: 1;
        }

        .field-icon {
          font-size: 1.2rem;
        }

        .admin-privileges-section {
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          border-radius: 15px;
          padding: 2rem;
          border: 2px solid ${theme.primary}20;
        }

        .section-title {
          color: ${theme.primary};
          font-weight: 700;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
        }

        .privileges-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .privilege-item {
          background: ${theme.lightGradient};
          padding: 1rem;
          border-radius: 10px;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-weight: 600;
          color: ${theme.primary};
          transition: all 0.3s ease;
        }

        .privilege-item:hover {
          transform: scale(1.05);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
        }

        .privilege-item i {
          font-size: 1.2rem;
        }

        .profile-card-footer {
          padding: 2rem;
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        }

        .change-password-btn {
          background: ${theme.gradient};
          border: none;
          border-radius: 25px;
          padding: 0.75rem 2rem;
          font-weight: 600;
          color: white;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 15px ${theme.primary}30;
        }

        .change-password-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px ${theme.primary}40;
          background: ${theme.gradient};
        }

        .last-updated {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        @keyframes avatarFloat {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes crownGlow {
          from {
            box-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
          }
          to {
            box-shadow: 0 0 20px rgba(255, 215, 0, 0.8);
          }
        }

        @keyframes adminBadgeGlow {
          from {
            box-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
          }
          to {
            box-shadow: 0 0 20px rgba(255, 255, 255, 0.6);
          }
        }

        @media (max-width: 768px) {
          .profile-hero-section .py-5 {
            padding: 2rem 0 !important;
          }

          .profile-avatar-container,
          .avatar-circle {
            width: 100px;
            height: 100px;
          }

          .avatar-circle {
            font-size: 2.5rem;
          }

          .admin-crown {
            width: 35px;
            height: 35px;
            font-size: 1rem;
            top: -8px;
            right: -3px;
          }

          .display-5 {
            font-size: 2rem !important;
          }

          .info-field {
            padding: 1rem;
          }

          .privileges-grid {
            grid-template-columns: 1fr;
          }

          .profile-card-footer {
            padding: 1.5rem;
          }

          .profile-card-footer .d-flex {
            flex-direction: column;
            gap: 1rem;
            text-align: center;
          }

          .change-password-btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}

export default Profile;
